import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Drawer from '../../components/Drawer/Drawer';
import Pagination from '../../components/Pagination/Pagination';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import styles from '../../styles/adminPanels.module.css';
import axiosClient from '../../api/axiosClient';

type RequestStatus = 'pending' | 'processing' | 'paid' | 'rejected';
type ActionStatus = 'processing' | 'paid' | 'rejected';

interface FundRequest {
  id: string;
  type: 'deposit' | 'withdrawal';
  amount: string;
  currency: string;
  crypto_amount: string | null;
  account_holder_name: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  upi_id: string | null;
  wallet_address: string | null;
  payment_method_type: string | null;
  package_name: string | null;
  transaction_ref: string | null;
  note: string | null;
  status: RequestStatus;
  created_at: string;
  full_name: string;
  email: string;
  admin_charge_pct: string | null;
  admin_charge_amount: string | null;
  gst_pct?: string | null;
  gst_amount?: string | null;
  paid_amount?: string | null;
  charge_kind?: string | null;
  net_payable_amount: string | null;
  payout_reference: string | null;
  payout_paid_at: string | null;
  payout_reviewed_at: string | null;
  payout_failure_reason: string | null;
}

const STATUS_COLORS: Record<RequestStatus, string> = {
  pending: '#e8a86a',
  processing: '#1d60f6',
  paid: '#5cb87d',
  rejected: '#e2534a',
};

const FILTERS = [
  'all',
  'pending',
  'processing',
  'paid',
  'rejected',
] as const;

const PAGE_SIZE = 8;

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 8,
  padding: '11px 12px',
  border: '1px solid var(--border, #64748b)',
  borderRadius: 8,
  background: 'var(--panel)',
  color: 'inherit',
  font: 'inherit',
};

function money(value: string | number | null | undefined, currency: string) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) return '—';

  const formatted = amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency === 'INR'
    ? `₹${formatted}`
    : `${currency} ${formatted}`;
}

function dateLabel(value: string | null) {
  if (!value) return '—';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('en-IN');
}

function toMinorUnits(value: string | number | null | undefined) {
  if (value === null || value === undefined) return null;

  const text = String(value).trim();

  if (!/^\d{1,12}(\.\d{1,2})?$/.test(text)) return null;

  const [whole, fraction = ''] = text.split('.');
  const result = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));

  return Number.isSafeInteger(result) ? result : null;
}

function hasValidBreakdown(request: FundRequest) {
  if (
    request.admin_charge_pct === null
    || request.admin_charge_pct === undefined
  ) {
    return false;
  }

  const gross = toMinorUnits(request.amount);
  const fee = toMinorUnits(request.admin_charge_amount);
  const net = toMinorUnits(request.net_payable_amount);
  const rate = Number(request.admin_charge_pct);

  return (
    gross !== null
    && fee !== null
    && net !== null
    && gross > 0
    && net > 0
    && Number.isFinite(rate)
    && rate >= 0
    && rate < 100
    && gross === fee + net
  );
}

const AdminFunds: React.FC = () => {
  const [requests, setRequests] = useState<FundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [requestType, setRequestType] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<FundRequest | null>(null);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  const [action, setAction] = useState<'paid' | 'rejected' | null>(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [confirmedNetAmount, setConfirmedNetAmount] = useState('');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [receivedTotal, setReceivedTotal] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const savingRef = useRef(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const params: Record<string, string> = {};

        if (filter !== 'all') params.status = filter;
        if (requestType !== 'all') params.type = requestType;

        const { data } = await axiosClient.get('/admin/funds', {
          params,
        });

        if (!active) return;

        setRequests(data.requests || []);
        setPage(1);
      } catch (error: any) {
        if (!active) return;

        setRequests([]);
        setLoadError(
          error.response?.data?.message || 'Could not load fund requests.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [filter, requestType, refreshKey]);

  const resetAction = () => {
    setAction(null);
    setPaymentReference('');
    setConfirmedNetAmount('');
    { setReceivedAmount(''); setReceivedTotal(''); };
    setRejectionReason('');
    setPaymentConfirmed(false);
  };

  const openRequest = (request: FundRequest) => {
    if (savingRef.current) return;

    resetAction();
    setSelected(request);
  };

  const closeRequest = () => {
    if (savingRef.current) return;

    setSelected(null);
    resetAction();
  };

  const updateStatus = async (status: ActionStatus) => {
    if (!selected || savingRef.current) return;

    const request = selected;
    const isWithdrawal = request.type === 'withdrawal';

    if (
      isWithdrawal
      && ['processing', 'paid'].includes(status)
      && !hasValidBreakdown(request)
    ) {
      toast.error('This withdrawal has no valid saved charge breakdown.');
      return;
    }

    const payload: {
      status: ActionStatus;
      payoutReference?: string;
      confirmedNetAmount?: string;
      receivedAmount?: string;
      receivedTotal?: string;
      rejectionReason?: string;
    } = { status };

    if (status === 'paid') {
      if (!paymentConfirmed) {
        toast.error(
          isWithdrawal
            ? 'Confirm that the payment was successfully completed.'
            : 'Confirm that the deposit was received.'
        );
        return;
      }

      if (isWithdrawal) {
        const reference = paymentReference.trim();

        if (!reference || reference.length > 200) {
          toast.error('Enter a payment reference of up to 200 characters.');
          return;
        }

        const enteredAmount = toMinorUnits(confirmedNetAmount);
        const expectedAmount = toMinorUnits(request.net_payable_amount);

        if (
          enteredAmount === null
          || enteredAmount <= 0
          || enteredAmount !== expectedAmount
        ) {
          toast.error(
            `Enter the exact net amount paid: ${
              money(request.net_payable_amount, request.currency || 'INR')
            }.`
          );
          return;
        }

        payload.payoutReference = reference;
        payload.confirmedNetAmount = confirmedNetAmount.trim();
      } else if (Number(request.gst_pct) > 0) {
        // GST deposit: the admin enters the TOTAL that reached the bank (amount + GST).
        const totalText = receivedTotal.trim();
        if (totalText) {
          const totalMinor = toMinorUnits(totalText);
          const expectedMinor = toMinorUnits(String(Number(request.amount) + Number(request.gst_amount || 0)));
          if (totalMinor === null || totalMinor <= 0 || expectedMinor === null || totalMinor > expectedMinor) {
            toast.error(`Total received must be more than 0 and not more than ${
              money(Number(request.amount) + Number(request.gst_amount || 0), request.currency || 'INR')
            }.`);
            return;
          }
          if (totalMinor !== expectedMinor) payload.receivedTotal = totalText;
        }
      } else {
        const received = receivedAmount.trim();

        if (received) {
          const receivedMinor = toMinorUnits(received);
          const requestedMinor = toMinorUnits(request.amount);

          if (
            receivedMinor === null
            || receivedMinor <= 0
            || requestedMinor === null
            || receivedMinor > requestedMinor
          ) {
            toast.error(
              `Received amount must be more than 0 and not more than ${
                money(request.amount, request.currency || 'INR')
              }.`
            );
            return;
          }

          if (receivedMinor !== requestedMinor) {
            payload.receivedAmount = received;
          }
        }
      }
    }

    if (status === 'rejected' && isWithdrawal) {
      const reason = rejectionReason.trim();

      if (!reason || reason.length > 2000) {
        toast.error('Enter a rejection reason of up to 2000 characters.');
        return;
      }

      payload.rejectionReason = reason;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      await axiosClient.patch(
        `/admin/funds/${request.id}/status`,
        payload
      );

      toast.success(`Request marked as ${status}.`);
      setSelected(null);
      resetAction();
      setRefreshKey((value) => value + 1);
      window.dispatchEvent(new Event('wallet:refresh'));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Could not update the request.'
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const isWithdrawal = selected?.type === 'withdrawal';
  const isOpenRequest = selected
    ? ['pending', 'processing'].includes(selected.status)
    : false;
  const breakdownValid = selected
    ? hasValidBreakdown(selected)
    : false;
  const currency = selected?.currency || 'INR';

  return (
    <AdminShell>
      <PageHeader
        title="Funds Review"
        subtitle="Review requests and record completed payments. Withdrawal charges are deducted from the requested amount."
        action={<AdminWalletBadge />}
      />

      <div className={styles.page}>
        <div className={styles.filters}>
          {FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.filterBtn} ${
                filter === item ? styles.filterBtnActive : ''
              }`}
              onClick={() => setFilter(item)}
              disabled={saving}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </button>
          ))}
        </div>

        <div className={styles.filters}>
          {['all', 'deposit', 'withdrawal'].map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.filterBtn} ${
                requestType === item ? styles.filterBtnActive : ''
              }`}
              onClick={() => setRequestType(item)}
              disabled={saving}
            >
              {item === 'all'
                ? 'All Types'
                : item === 'deposit'
                  ? 'Deposits'
                  : 'Withdrawals'}
            </button>
          ))}
        </div>

        <div className={styles.tableWrap} style={{ overflowX: 'auto' }}>
          {loading && (
            <div className={styles.loading}>
              <span className={styles.spinnerLg} />
            </div>
          )}

          {!loading && loadError && (
            <div className={styles.empty}>
              <p role="alert">{loadError}</p>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => setRefreshKey((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !loadError && requests.length === 0 && (
            <div className={styles.empty}>
              No requests match these filters.
            </div>
          )}

          {!loading && !loadError && requests.length > 0 && (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Type</th>
                    <th>Requested</th>
                    <th>Admin Charge</th>
                    <th>Net Payable</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {requests
                    .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
                    .map((request) => {
                      const requestCurrency = request.currency || 'INR';
                      const withdrawal = request.type === 'withdrawal';

                      return (
                        <tr key={request.id}>
                          <td>
                            <div className={styles.nameCell}>
                              {request.full_name}
                            </div>
                            <div className={styles.subCell}>
                              {request.email}
                            </div>
                          </td>

                          <td>
                            <span style={{ textTransform: 'capitalize' }}>
                              {request.type}
                            </span>
                            {request.package_name && (
                              <div className={styles.subCell}>
                                {request.package_name}
                              </div>
                            )}
                          </td>

                          <td>
                            {money(request.amount, requestCurrency)}
                          </td>

                          <td>
                            {withdrawal ? (
                              <>
                                {money(
                                  request.admin_charge_amount,
                                  requestCurrency
                                )}
                                {request.admin_charge_pct != null && (
                                  <div className={styles.subCell}>
                                    {Number(request.admin_charge_pct)}%
                                  </div>
                                )}
                                {request.status === 'rejected' && (
                                  <div className={styles.subCell}>
                                    Not collected
                                  </div>
                                )}
                              </>
                            ) : (request.gst_pct != null && Number(request.gst_pct) > 0 ? (
                              <>
                                GST {Number(request.gst_pct)}%: {money(request.gst_amount, requestCurrency)}
                                <div className={styles.subCell}>
                                  User pays {money(Number(request.amount) + Number(request.gst_amount || 0), requestCurrency)}
                                </div>
                              </>
                            ) : '—')}
                          </td>

                          <td>
                            {withdrawal
                              ? money(
                                  request.net_payable_amount,
                                  requestCurrency
                                )
                              : '—'}
                          </td>

                          <td>{request.payment_method_type || '—'}</td>

                          <td>
                            <span
                              className={styles.statusPill}
                              style={{
                                color: STATUS_COLORS[request.status],
                                borderColor: STATUS_COLORS[request.status],
                              }}
                            >
                              {request.status}
                            </span>
                          </td>

                          <td>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => openRequest(request)}
                            >
                              Review
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={Math.max(
                  1,
                  Math.ceil(requests.length / PAGE_SIZE)
                )}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </div>

      <Drawer
        open={!!selected}
        onClose={closeRequest}
        title="Fund Request"
      >
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Account</div>
              <div className={drawerStyles.detailValue}>
                {selected.full_name}
              </div>
              <div className={styles.subCell}>{selected.email}</div>
              <div
                className={styles.subCell}
                style={{ overflowWrap: 'anywhere', marginTop: 6 }}
              >
                Request ID: {selected.id}
              </div>
            </div>

            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Type</div>
                <div
                  className={drawerStyles.detailValue}
                  style={{ textTransform: 'capitalize' }}
                >
                  {selected.type}
                </div>
              </div>

              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Status</div>
                <span
                  className={styles.statusPill}
                  style={{
                    color: STATUS_COLORS[selected.status],
                    borderColor: STATUS_COLORS[selected.status],
                  }}
                >
                  {selected.status}
                </span>
              </div>

              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>
                  Requested Amount
                </div>
                <div className={drawerStyles.detailValue}>
                  {money(selected.amount, currency)}
                </div>
              </div>

              {!isWithdrawal && selected.gst_pct != null && Number(selected.gst_pct) > 0 && (
                <div className={drawerStyles.detailRow}>
                  <div className={drawerStyles.detailLabel}>GST on this deposit</div>
                  <div className={drawerStyles.detailValue}>
                    {Number(selected.gst_pct)}% = {money(selected.gst_amount, currency)}
                    {' '}· User should have paid <b>{money(Number(selected.amount) + Number(selected.gst_amount || 0), currency)}</b>
                    {' '}(amount + GST). Only the amount becomes the investment.
                  </div>
                </div>
              )}

              {!isWithdrawal && selected.paid_amount != null && (
                <div className={drawerStyles.detailRow}>
                  <div className={drawerStyles.detailLabel}>User says he paid</div>
                  <div className={drawerStyles.detailValue}>
                    <b>{money(selected.paid_amount, currency)}</b>
                    {Math.abs(Number(selected.paid_amount) - (Number(selected.amount) + Number(selected.gst_amount || 0))) > 0.005 && (
                      <span style={{ color: '#e2534a', fontWeight: 700 }}> · Mismatch with amount + GST</span>
                    )}
                    {' '}— match this with your bank statement.
                  </div>
                </div>
              )}

              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Method</div>
                <div className={drawerStyles.detailValue}>
                  {selected.payment_method_type || '—'}
                </div>
              </div>

              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Submitted</div>
                <div className={drawerStyles.detailValue}>
                  {dateLabel(selected.created_at)}
                </div>
              </div>

              {selected.package_name && (
                <div className={drawerStyles.detailRow}>
                  <div className={drawerStyles.detailLabel}>Purpose</div>
                  <div className={drawerStyles.detailValue}>
                    {selected.package_name}
                  </div>
                </div>
              )}
            </div>

            {isWithdrawal && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>
                  Payout Breakdown
                </div>

                {breakdownValid ? (
                  <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                    <div>
                      Requested: <b>{money(selected.amount, currency)}</b>
                    </div>
                    <div>
                      Admin charge ({Number(selected.admin_charge_pct)}%):
                      {' '}
                      <b>{money(selected.admin_charge_amount, currency)}</b>
                    </div>
                    <div>
                      Net payable:
                      {' '}
                      <b>{money(selected.net_payable_amount, currency)}</b>
                    </div>
                    <div className={styles.subCell}>
                      The charge is included in the requested amount.
                    </div>
                  </div>
                ) : (
                  <p role="alert" style={{ color: '#e8a86a' }}>
                    No valid charge breakdown is saved for this request.
                    An open request must be rejected and submitted again
                    before payment.
                  </p>
                )}

                {selected.status === 'rejected' && (
                  <p className={styles.subCell}>
                    No payout or admin charge was collected for this request.
                  </p>
                )}
              </div>
            )}

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>
                {isWithdrawal ? 'Receiving Details' : 'Payment Details'}
              </div>

              {selected.account_holder_name && (
                <div className={drawerStyles.detailValue}>
                  Holder: {selected.account_holder_name}
                </div>
              )}

              {selected.account_number && (
                <div className={drawerStyles.detailValue}>
                  Account: {selected.account_number}
                </div>
              )}

              {selected.ifsc_code && (
                <div className={drawerStyles.detailValue}>
                  IFSC: {selected.ifsc_code}
                </div>
              )}

              {selected.upi_id && (
                <div className={drawerStyles.detailValue}>
                  UPI: {selected.upi_id}
                </div>
              )}

              {selected.wallet_address && (
                <div
                  className={drawerStyles.detailValue}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  Wallet: {selected.wallet_address}
                </div>
              )}

              {selected.crypto_amount && (
                <div className={styles.subCell}>
                  Submitted crypto amount: {selected.crypto_amount} USDT
                </div>
              )}

              {selected.transaction_ref && (
                <div
                  className={drawerStyles.detailValue}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  Submitted reference: {selected.transaction_ref}
                </div>
              )}

              {!selected.wallet_address
                && !selected.account_number
                && !selected.upi_id
                && !selected.transaction_ref && (
                  <div className={styles.subCell}>
                    No payment details recorded.
                  </div>
                )}
            </div>

            {selected.note && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Note</div>
                <div className={drawerStyles.detailValue}>
                  {selected.note}
                </div>
              </div>
            )}

            {selected.payout_reference && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>
                  Payout Reference
                </div>
                <div
                  className={drawerStyles.detailValue}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {selected.payout_reference}
                </div>
              </div>
            )}

            {selected.payout_paid_at && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Paid At</div>
                <div className={drawerStyles.detailValue}>
                  {dateLabel(selected.payout_paid_at)}
                </div>
              </div>
            )}

            {selected.payout_reviewed_at && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Reviewed At</div>
                <div className={drawerStyles.detailValue}>
                  {dateLabel(selected.payout_reviewed_at)}
                </div>
              </div>
            )}

            {selected.payout_failure_reason && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Reason</div>
                <div className={drawerStyles.detailValue}>
                  {selected.payout_failure_reason}
                </div>
              </div>
            )}

            {isOpenRequest && action === 'paid' && (
              <div className={drawerStyles.detailRow}>
                <h3>
                  {isWithdrawal ? 'Record Completed Payment' : 'Confirm Deposit'}
                </h3>

                {isWithdrawal && (
                  <>
                    <p>
                      Net amount to be paid:
                      {' '}
                      <b>{money(selected.net_payable_amount, currency)}</b>
                    </p>

                    <label
                      htmlFor="fund-payout-reference"
                      style={{ display: 'block', marginTop: 16 }}
                    >
                      Payment Reference / UTR
                    </label>
                    <input
                      id="fund-payout-reference"
                      style={inputStyle}
                      value={paymentReference}
                      onChange={(event) => setPaymentReference(event.target.value)}
                      maxLength={200}
                      disabled={saving}
                      placeholder="Enter the completed payment reference"
                    />

                    <label
                      htmlFor="fund-paid-amount"
                      style={{ display: 'block', marginTop: 16 }}
                    >
                      Actual Net Amount Paid ({currency})
                    </label>
                    <input
                      id="fund-paid-amount"
                      style={inputStyle}
                      type="text"
                      inputMode="decimal"
                      value={confirmedNetAmount}
                      onChange={(event) => setConfirmedNetAmount(event.target.value.replace(/[^\d.]/g, ''))}
                      disabled={saving}
                      placeholder="Enter the actual amount paid"
                    />
                  </>
                )}

                {!isWithdrawal && Number(selected?.gst_pct) > 0 && (
                  <>
                    <label htmlFor="fund-received-total" style={{ display: 'block', marginTop: 16 }}>
                      Total that reached the bank, amount + GST ({currency}) - leave empty if it is the full{' '}
                      {money(Number(selected?.amount) + Number(selected?.gst_amount || 0), currency)}
                    </label>
                    <input
                      id="fund-received-total"
                      style={inputStyle}
                      type="text"
                      inputMode="decimal"
                      value={receivedTotal}
                      onChange={(event) => setReceivedTotal(event.target.value.replace(/[^\d.]/g, ''))}
                      disabled={saving}
                      placeholder="Only fill this if less money arrived"
                    />
                    <div style={{ fontSize: 11.5, opacity: .75, marginTop: 4 }}>
                      If less arrived, the investment becomes that total without GST.
                    </div>
                  </>
                )}

                {!isWithdrawal && !(Number(selected?.gst_pct) > 0) && (
                  <>
                    <label
                      htmlFor="fund-received-amount"
                      style={{ display: 'block', marginTop: 16 }}
                    >
                      Actual investment amount received, without GST ({currency}) - leave empty if it is the full {money(selected?.amount, currency)}
                    </label>
                    <input
                      id="fund-received-amount"
                      style={inputStyle}
                      type="text"
                      inputMode="decimal"
                      value={receivedAmount}
                      onChange={(event) => setReceivedAmount(event.target.value.replace(/[^\d.]/g, ''))}
                      disabled={saving}
                      placeholder="Only fill this if less money arrived"
                    />
                  </>
                )}

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    marginTop: 18,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={paymentConfirmed}
                    onChange={(event) => setPaymentConfirmed(event.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    {isWithdrawal
                      ? 'I confirm the payment was successfully completed.'
                      : 'I confirm the deposit was received and verified.'}
                  </span>
                </label>

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={saving || !paymentConfirmed}
                    onClick={() => updateStatus('paid')}
                  >
                    {saving ? 'Saving…' : 'Confirm Paid'}
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={saving}
                    onClick={resetAction}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {isOpenRequest && action === 'rejected' && (
              <div className={drawerStyles.detailRow}>
                <h3>Reject Request</h3>

                {isWithdrawal ? (
                  <>
                    <p>
                      Reject only if no payment was made and no transfer is
                      still pending. The full reserved amount will be released.
                    </p>
                    <label htmlFor="fund-rejection-reason">
                      Rejection Reason
                    </label>
                    <textarea
                      id="fund-rejection-reason"
                      style={inputStyle}
                      rows={4}
                      maxLength={2000}
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      disabled={saving}
                      placeholder="Explain why the withdrawal is rejected"
                    />
                  </>
                ) : (
                  <p>Confirm that this deposit request should be rejected.</p>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button
                    type="button"
                    className={styles.actionBtnReject}
                    disabled={
                      saving || (isWithdrawal && !rejectionReason.trim())
                    }
                    onClick={() => updateStatus('rejected')}
                  >
                    {saving ? 'Saving…' : 'Confirm Rejection'}
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={saving}
                    onClick={resetAction}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {isOpenRequest && !action && (
              <div
                className={drawerStyles.footer}
                style={{
                  position: 'sticky',
                  bottom: 0,
                  background: 'var(--panel)',
                  margin: '24px -24px -24px',
                  flexWrap: 'wrap',
                }}
              >
                {selected.status === 'pending' && (
                  <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={saving || (isWithdrawal && !breakdownValid)}
                    onClick={() => updateStatus('processing')}
                  >
                    {saving ? 'Saving…' : 'Mark Processing'}
                  </button>
                )}

                <button
                  type="button"
                  className={styles.actionBtn}
                  disabled={saving || (isWithdrawal && !breakdownValid)}
                  onClick={() => {
                    resetAction();
                    setAction('paid');
                  }}
                >
                  Record Paid
                </button>

                <button
                  type="button"
                  className={styles.actionBtnReject}
                  disabled={saving}
                  onClick={() => {
                    resetAction();
                    setAction('rejected');
                  }}
                >
                  Reject
                </button>
              </div>
            )}
          </>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminFunds;